/**
 * External dependencies
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import cx from 'classnames';
import { usePopper } from 'react-popper';

/**
 * Internal Dependencies
 */
import Icon from '@/common/components/icon/icon';

export default function InputStays({
    type = 'text',
    name,
    value,
    className = '',
    required,
    handleChange = () => {},
    handleBlur = () => {},
    placeholder,
    errorText,
    showErrorBorder,
    label,
    title,
    id,
    validation = false,
    isValid = false,
    disabled = false,
    suggestions = [],
    suggestionsIcon = 'search',
    fieldIcon,
    onSuggestionClick = () => {},
    children,
    loadChildrenToSuggestions = true,
    addPopperStyles = true,
    showValidIcon = true,
    ...props
}) {
    const wrapper = useRef(null);
    const input = useRef(null);
    const [focused, setFocused] = useState(false);
    const [valueRegExp, setValueRegExp] = useState(new RegExp());

    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);

    const modifiers = useMemo(
        () => [
            {
                name: 'sameWidth',
                enabled: true,
                fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                phase: 'beforeWrite',
                requires: ['computeStyles'],
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 8],
                },
            },
        ],
        []
    );

    const { styles, attributes, update, forceUpdate } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        strategy: 'fixed',
        modifiers,
    });

    useEffect(() => {
        const handleClick = (event) => {
            const closest = event.target.closest('.field--stays');
            if (!closest || closest !== wrapper?.current) {
                setFocused(false);
            }
        };

        window.addEventListener('click', handleClick);

        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, []);

    useEffect(() => {
        if (suggestions.length === 0) {
            return;
        }

        if (typeof update === 'function') {
            update();
        }
    }, [suggestions]);

    useEffect(() => {
        const escSpecialCharValue = value.replace(/[-\/\\^$*+?.()|[\]{}]/g, '');

        setValueRegExp(new RegExp(escSpecialCharValue + '(?!([^<]+)?<)', 'gi'));
    }, [value]);

    const handleFocusWithin = () => {
        setFocused(true);
    };

    const handleSuggestionClick = (item) => {
        onSuggestionClick(item);

        setFocused(false);
    };

    const fieldSuggestionsAdditionalProps = addPopperStyles ? attributes.popper : {};

    const customStyles = {
        transform: `translateY(${input?.current?.offsetHeight || 50}px)`,
    };

    return (
        <div
            ref={wrapper}
            className={cx('field field--stays', className, {
                'is-focused': focused,
                'no-popper': !addPopperStyles,
                'field--error-border': showErrorBorder && errorText,
            })}
        >
            {fieldIcon && <span className="field__svg">{fieldIcon}</span>}

            {title && <span className="field__title">{title}</span>}

            <div className="field__control" ref={setReferenceElement}>
                <input
                    type={type}
                    ref={input}
                    id={id}
                    name={name}
                    value={value}
                    placeholder={label ? label : placeholder}
                    className="field__input"
                    required={required}
                    disabled={disabled}
                    onFocus={handleFocusWithin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    {...props}
                />

                {label && (
                    <label className="field__label" htmlFor={id}>
                        {label}
                    </label>
                )}

                {!!errorText && <p className="field__error">{errorText}</p>}
            </div>

            <div
                className="field__suggestions"
                ref={setPopperElement}
                style={addPopperStyles ? styles.popper : customStyles}
                {...fieldSuggestionsAdditionalProps}
            >
                <ul>
                    {suggestions.map((item, index) => (
                        <li key={index}>
                            <button
                                type="button"
                                className="form-search__suggestion"
                                onClick={() => handleSuggestionClick(item)}
                            >
                                <Icon name={suggestionsIcon} size={17} />

                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: item?.description?.replace(valueRegExp, '<span>$&</span>'),
                                    }}
                                />
                            </button>
                        </li>
                    ))}

                    <div className="field__suggestions-children">{loadChildrenToSuggestions && children}</div>
                </ul>
            </div>

            {isValid && showValidIcon && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M8 16C12.4062 16 16 12.4375 16 8C16 3.59375 12.4062 0 8 0C3.5625 0 0 3.59375 0 8C0 12.4375 3.5625 16 8 16ZM11.5312 6.53125L7.53125 10.5312C7.21875 10.8438 6.75 10.8438 6.46875 10.5312L4.46875 8.53125C4.15625 8.25 4.15625 7.78125 4.46875 7.5C4.75 7.1875 5.21875 7.1875 5.53125 7.5L7 8.96875L10.4688 5.46875C10.75 5.1875 11.2188 5.1875 11.5 5.46875C11.8125 5.78125 11.8125 6.25 11.5 6.53125H11.5312Z"
                        fill="#58BE00"
                    />
                </svg>
            )}
        </div>
    );
}
